<template>
  <div
    :id="`Header1-${pageUid}`"
    class="header header1 header--1 lp-section header-container width-expand relative z-level-5">
    <div class="header-spacer"></div>

    <div
      :class="`c-${textColor} bg-${backgroundColor}`"
      class="header-content margin-inline-auto fixed-top-strip flex-middle width-expand">
      <div class="container container-l ">
        <component
          v-if="!isMobile"
          :is="'block'"
          class="gap-xl flex-middle width-expand">
          <div
            class="logos flex-middle gap-m"
            v-if="(logo && logo.show) || (logo2 && logo2.show)">
            <anchor
              v-if="logo.show"
              :href="sanitizeLink(logo.href)"
              :target="logo.target"
              class="logo inline-block logo logo--1 flex-middle object-contain"
              @click.prevent="
                trackEvent('header1|logo1|click').then(logo.navigationHandler)
              ">
              <img class="img" :alt="logo.alt" :src="logo.finalSrc" />
            </anchor>
            <anchor
              v-if="logo2.show"
              :href="sanitizeLink(logo2.href)"
              :target="logo2.target"
              class="logo-2 inline-block logo logo--2 flex-middle object-contain"
              @click.prevent="
                trackEvent('header1|logo2|click').then(logo2.navigationHandler)
              ">
              <img class="img" :alt="logo2.alt" :src="logo2.finalSrc" />
            </anchor>
          </div>
          <div
            class="menu padding-s-vertical l:padding-m-vertical"
            v-if="showMenu">
            <ul class="unstyled-list flex-middle gap-l">
              <li v-for="item in menuItems" :key="item.to">
                <a
                  @click.prevent="
                      track('header1|menuLink|click', {
                        value: item.label,
                      }).then(activateMenu(item.to))
                    "
                  :href="item.to"
                  class="text-l item-link"
                  :class="{
                    [`c-${textColor}`]: true,
                    active: menuScrollSpy?.hashMap[item.target]?.isActive,
                    'exact-active':
                      menuScrollSpy?.hashMap[item.target]?.isExactActive,
                  }"
                  >{{ item.label }}</a
                >
              </li>
              <li
                v-for="(extraLink, linkIndex) in extraMenuLinks"
                :key="`link-${linkIndex}`">
                <a
                  :href="
                      isNonEmptyString(extraLink.href) ? extraLink.href : '#'
                    "
                  class="text-l item-link cursor-pointer"
                  :class="{
                      [`c-${textColor}`]: true,
                    }"
                  @click.prevent="
                      closeMenuAsync().then(
                        trackEvent('header1|menuLink|click', {
                          value: extraLink.text,
                        }).then(extraLink.navigationHandler)
                      )
                    "
                >{{ extraLink.text }}</a
                >
              </li>
            </ul>
          </div>
          <div
            class="actions margin-auto-inline-start flex-middle gap-l padding-s-vertical l:padding-m-vertical">
            <div
              class="buttons flex-middle gap-m"
              v-if="(button && button.show) || (button2 && button2.show)">
              <form-button
                v-show="button.show"
                class=""
                v-bind="button"
                :auto-translate="false"
                @click="
                  track('header1|button1|click', { value: button.text }).then(
                    button.navigationHandler
                  )
                "></form-button>
              <form-button
                v-show="button2.show"
                class=""
                v-bind="button2"
                :auto-translate="false"
                @click="
                  track('header1|button2|click', { value: button2.text }).then(
                    button2.navigationHandler
                  )
                "></form-button>
            </div>
            <div
              class="links flex-middle gap-m"
              v-if="(link && link.show) || (link2 && link2.show)">
              <anchor
                v-if="link.show && !hasDropdownLinks"
                :href="sanitizeLink(link.href)"
                :target="link.target"
                :class="`c-${textColor}`"
                class="action-link text-l inline-block action-link--1 flex-middle object-contain relative"
                @click.prevent="
                  track('header1|link1|click', { value: link.text }).then(
                    link.navigationHandler
                  )
                ">
                <icon-text v-bind="link" :class="`c-${textColor}`"></icon-text>
              </anchor>

              <span
                v-if="link.show && hasDropdownLinks"
                :href="sanitizeLink(link.href)"
                :target="link.target"
                :class="`c-${textColor}`"
                @mouseenter="
                  track('header1|link1DropdownRoot|mouseenter', {
                    value: link.text,
                  })
                "
                class="cursor-pointer action-link text-l inline-block action-link--1 flex-middle object-contain relative">
                <icon-text v-bind="link"></icon-text>
                <div
                  v-if="hasDropdownLinks"
                  :class="`bg-${backgroundColor}`"
                  class="link-dropdown absolute padding-l animate__fadeInUpSmall animate--fastest">
                  <ul class="unstyled-list flex-column gap-l">
                    <li
                      v-for="link in linkDropdown"
                      :key="link.href + link.label">
                      <a
                        class="text-l"
                        :class="`c-${textColor}`"
                        :href="sanitizeLink(link.href)"
                        @click="
                          track('header1|linkInDropdown|click', {
                            value: link.label,
                          })
                        "
                        :target="
                          isNonEmptyString(link.target) ? link.target : '_self'
                        ">
                        {{ link.label }}
                      </a>
                    </li>
                  </ul>
                </div>
              </span>

              <anchor
                v-if="link2.show"
                :href="sanitizeLink(link2.href)"
                :target="link2.target"
                :class="`c-${textColor}`"
                class="action-link text-l inline-block action-link--1 flex-middle object-contain"
                @click.prevent="
                  track('header1|link2|click', { value: link.text }).then(
                    link2.navigationHandler
                  )
                ">
                <icon-text v-bind="link2" :class="`c-${textColor}`"></icon-text>
              </anchor>
            </div>
          </div>
        </component>

        <component
          v-if="isMobile"
          :is="'block'"
          class="gap-xl flex-middle width-expand">
          <div class="menu-trigger" v-show="isMobile">
            <icon
              class="cursor-pointer animate__fadeIn animate--fastest"
              v-show="!isMenuOpen"
              icon="align-justify-alt"
              size="3"
              @click="
                track('header1|mobileMenuToggle|click', {
                  value: isMenuOpen,
                }).then((isMenuOpen = !isMenuOpen))
              "></icon>
            <icon
              class="cursor-pointer animate__fadeIn animate--fastest"
              v-show="isMenuOpen"
              icon="x"
              size="3"
              @click="isMenuOpen = !isMenuOpen"></icon>
          </div>
          <div class="logos width-expand flex-middle flex-center">
            <anchor
              v-if="logo.show"
              :href="sanitizeLink(logo.href)"
              :target="logo.target"
              class="logo inline-block logo logo--1 flex-middle object-contain"
              @click.prevent="
                trackEvent('header1|logo1|click').then(logo.navigationHandler)
              ">
              <img class="img" :alt="logo.alt" :src="logo.finalSrc" />
            </anchor>
          </div>
          <div class="actions">
            <div
              v-if="link?.show"
              class="links flex-middle gap-m">
              <anchor
                v-if="link.show && !hasDropdownLinks"
                :href="sanitizeLink(link.href)"
                :target="link.target"
                :class="`c-${textColor}`"
                class="action-link text-l inline-block action-link--1 flex-middle object-contain relative"
                @click.prevent="
                  track('header1|link1|click', { value: link.text }).then(
                    link.navigationHandler
                  )
                ">
                <icon v-bind="link" :class="`c-${textColor}`"></icon>
              </anchor>

              <span
                v-if="link.show && hasDropdownLinks"
                :href="sanitizeLink(link.href)"
                :target="link.target"
                :class="`c-${textColor}`"
                @mouseenter="
                  track('header1|link1DropdownRoot|mouseenter', {
                    value: link.text,
                  })
                "
                class="cursor-pointer action-link text-l inline-block action-link--1 flex-middle object-contain relative">
                <icon v-bind="link" v-if="!isLinkDropdownOpened" @click="isLinkDropdownOpened = true"></icon>
                <icon v-bind="link" icon="x" v-if="isLinkDropdownOpened"  @click="isLinkDropdownOpened = false"></icon>
                <div
                  v-if="hasDropdownLinks"
                  v-click-outside="() => {isLinkDropdownOpened = false}"
                  v-show="isLinkDropdownOpened"
                  :class="`bg-${backgroundColor}`"
                  class="link-dropdown link-dropdown--mobile absolute padding-l animate__fadeInUpSmall animate--fastest">
                  <ul class="unstyled-list flex-column gap-l">
                    <li
                      v-for="link in linkDropdown"
                      :key="link.href + link.label">
                      <a
                        class="text-l"
                        :class="`c-${textColor}`"
                        :href="sanitizeLink(link.href)"
                        @click="
                          track('header1|linkInDropdown|click', {
                            value: link.label,
                          })
                        "
                        :target="
                          isNonEmptyString(link.target) ? link.target : '_self'
                        ">
                        {{ link.label }}
                      </a>
                    </li>
                  </ul>
                </div>
              </span>
            </div>

          </div>
          <drawer v-model="isMenuOpen"
                  width="360px"
                  :class="`bg-${backgroundColor}`" class="header-1-drawer">
            <anchor
              v-if="logo.show"
              :href="sanitizeLink(logo.href)"
              :target="logo.target"
              width="340px"
              class="block logo logo--mobile-drawer margin-l-bottom inline-block logo logo--1 flex-middle object-contain"
              @click.prevent="
                trackEvent('header1|logo1|click').then((e) => {
                  isMenuOpen = false;
                  logo.navigationHandler(e);
                })
              ">
              <img class="img" :alt="logo.alt" :src="logo.finalSrc" />
            </anchor>

            <div class="menu" v-if="showMenu">
              <ul class="unstyled-list flex-column gap-m">
                <li v-for="item in menuItems" :key="item.to">
                  <a
                    @click.prevent="
                      track('header1|menuLink|click', {
                        value: item.label,
                      }).then(activateMenu(item.to))
                    "
                    :href="item.to"
                    class="text-l block padding bg-gray-1 border-radius-s"
                    :class="`c-${textColor}`"
                    >{{ item.label }}</a
                  >
                </li>
                <li
                  v-for="(extraLink, linkIndex) in extraMenuLinks"
                  :key="`link-${linkIndex}`">
                  <a
                    :href="
                      isNonEmptyString(extraLink.href) ? extraLink.href : '#'
                    "
                    class="text-l item-link cursor-pointer"
                    :class="{
                      [`c-${textColor}`]: true,
                    }"
                    @click.prevent="
                      closeMenuAsync().then(
                        trackEvent('header1|menuLink|click', {
                          value: extraLink.text,
                        }).then(extraLink.navigationHandler)
                      )
                    "
                    >{{ extraLink.text }}</a
                  >
                </li>
              </ul>
            </div>
            <div
              class="buttons flex-column flex-middle gap-m margin-xl-top"
              v-if="(button && button.show) || (button2 && button2.show)">
              <form-button
                v-show="button.show"
                class="width-expand"
                v-bind="button"
                :auto-translate="false"
                :size="'m'"
                @click="
                  track('header1|button1|click', { value: button.text }).then(
                    button.navigationHandler
                  )
                "></form-button>
              <form-button
                v-show="button2.show"
                class="width-expand"
                v-bind="button2"
                :auto-translate="false"
                :size="'m'"
                @click="
                  track('header1|button2|click', { value: button2.text }).then(
                    button2.navigationHandler
                  )
                "></form-button>
            </div>

            <hr class="margin-l-vertical" />
            <div class="">
              <anchor
                v-if="link.show"
                :href="sanitizeLink(link.href)"
                :target="link.target"
                :class="`c-${textColor}`"
                class="action-link text-l inline-block action-link--1 flex-middle object-contain relative"
                @click.prevent="
                  track('header1|link1|click', { value: link.text }).then(
                    link.navigationHandler
                  )
                ">
                <icon-text
                  class="width-expand flex-center"
                  v-bind="link"
                  :class="`c-${textColor}`"></icon-text>
              </anchor>

              <anchor
                v-if="link2.show"
                :href="sanitizeLink(link2.href)"
                :target="link2.target"
                :class="`c-${textColor}`"
                class="margin-l-top action-link text-l inline-block action-link--1 flex-middle object-contain"
                @click.prevent="
                  track('header1|link2|click', { value: link.text }).then(
                    link2.navigationHandler
                  )
                ">
                <icon-text v-bind="link2" :class="`c-${textColor}`"></icon-text>
              </anchor>
            </div>
          </drawer>
        </component>
      </div>
    </div>
  </div>
</template>

<script>
import useUiElements from "@/client/extensions/composition/useUiElements";
import lpSectionMixin from "@/client/applications/meitavlp/extensions/mixins/lpSection";
import { inject, computed } from "vue";
import useDom from "@/client/extensions/composition/useDom";

export default {
  mixins: [lpSectionMixin],
  setup: function (props) {
    let { scrollToElement, isSmallerThanL, displayWidth, createScrollSpy } =
      useDom();

    let pageUid = inject("pageUid");
    let menuItems = computed(() => {
      let result = [];
      if (!Array.isArray(props.pageData.types)) {
        return result;
      }

      props.pageData.types.forEach((type) => {
        if (
          utilities.hasProperty(props.pageData[type], "menuLabel") &&
          utilities.isNonEmptyString(props.pageData[type].menuLabel)
        ) {
          let target = `${type}-${pageUid}`;
          result.push({
            label: props.pageData[type].menuLabel,
            target: target,
            to: `#${target}`,
          });
        }
      });

      return result;
    });
    let scrollSpyItems = menuItems.value.map((item) => item.target);

    let menuScrollSpy = createScrollSpy(scrollSpyItems, { offset: 160 });

    let { wrapUiElement } = useUiElements({});
    let overloadedProps = useUiElements({ input: props.sectionData }).props;

    try {
      overloadedProps.extraMenuLinks = overloadedProps.extraMenuLinks.map(
        (extraLink) => {
          return wrapUiElement(extraLink);
        }
      );
    } catch (e) {
      console.log(e);
    }
    let schema = {
      allData: {},
      pageUid: "",
      mobileBreakPoint: 1200,
      trackEvent: () => {},
      track: () => {},
      title: "",
      subTitle: "",
      textColor: "",
      backgroundColor: "",
      logo: {},
      logo2: {},
      showMenu: false,
      extraMenuLinks: [],
      button: {},
      button2: {},
      link: {},
      link2: {},
      dropdownLinks: [],
      scrollToElement: () => {},
      isSmallerThanL: false,
      linkDropdown: [],
      displayWidth: 1200,
      menuItems,
      menuScrollSpy: {},
      triggerSectionsEvent: () => {},
    };
    return {
      ...schema,
      ...overloadedProps,
      allData: overloadedProps,
      pageUid,
      trackEvent: inject("trackEvent"),
      track: inject("track"),
      scrollToElement,
      isSmallerThanL,
      displayWidth,
      menuItems,
      menuScrollSpy,
      triggerSectionsEvent: inject("triggerSectionsEvent"),
    };
  },
  data: function () {
    return {
      isMenuOpen: false,
      isLinkDropdownOpened: false,
    };
  },
  computed: {
    isMobile() {
      return this.displayWidth < this.mobileBreakPoint;
    },
    hasDropdownLinks() {
      return Array.isArray(this.linkDropdown) && this.linkDropdown.length > 0;
    },
  },
  methods: {
    closeMenu() {
      this.isMenuOpen = false;
      return this;
    },
    async closeMenuAsync() {
      return new Promise((resolve, reject) => {
        this.isMenuOpen = false;
        resolve();
      });
    },
    activateMenu(to) {
      this.isMenuOpen = false;
      this.scrollToElement(to);
    },
    lpReadyHandler() {
      this.menuScrollSpy.setup();
      this.menuScrollSpy.update();
    },
  },
  mounted() {
    setTimeout(() => {
      this.triggerSectionsEvent("testEvent", { caller: this, test: 123 });
    }, 1000);
  },
};
</script>

<style scoped lang="scss">
.header1 {
  --header-height: 80px;

  .header-content {
    width: 100%;
    min-width: 100%;
    top: 0;
    inset-inline-start: 0;
    height: var(--header-height);
    box-shadow: var(--box-shadow-1);
  }

  .header-spacer {
    height: var(--header-height);
  }

  .item-link {
    &.exact-active {
      font-weight: bold;
    }
  }

  .logo,
  .logo2 {
    display: flex;
    align-items: center;
    height: var(--header-height);
    max-height: var(--header-height);
    max-height: calc(var(--header-height) - 2 * var(--margin-m));
    img {
      object-fit: contain;
      max-height: 100%;
      width: auto;
    }
  }



  .action-link {
    // better dropdown positioning
    padding-block: var(--margin-m);
  }

  .action-link:hover .link-dropdown{
    //
    display: block;
    pointer-events: auto;
  }

  .link-dropdown {
    display: none;
    pointer-events: none;
    position: absolute;
    top: 100%;
    inset-inline-end: 0;
    min-width: 250px;
    // just to make sure we dont go over the edge
    max-width: calc(100lvw - 60px);
    border-radius: var(--border-radius-s);
    z-index: var(--z-top-1);
    border: 1px solid var(--c-gray-1);
    box-shadow: var(--box-shadow-2);

    &--mobile {
      display: block;
      pointer-events: auto;
    }
  }


}
</style>

<style lang="scss">
.header-1-drawer .logo.logo--mobile-drawer {
  max-height: 60px;
  max-width: clamp(0px, 300px, 100%);
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;

  img {
    object-fit: contain;
    max-width: 100%;
    max-height: 60px;
    width: auto;
  }
}
</style>
